import { Head, Link, router } from "@inertiajs/react"
import { useState, useEffect, useRef, useCallback } from "react"
import Markdown from "react-markdown"
import { track } from "@/lib/analytics"
import type { Listing, Video, SocialConnection, SocialPost } from "@/types"
import cable from "@/lib/cable"
import DashboardLayout from "@/components/dashboard/layout"
import ImportProgress from "@/components/dashboard/import-progress"
import ImageLightbox from "@/components/dashboard/image-lightbox"
import ListingMap from "@/components/dashboard/listing-map"
import VideoCard from "@/components/dashboard/videos/video-card"
import ShareListingDialog from "@/components/dashboard/listings/share-listing-dialog"
import SocialPostDetailDialog from "@/components/dashboard/social-post-detail-dialog"
import SocialPostMetrics from "@/components/dashboard/videos/social-post-metrics"
import PlatformIcon from "@/components/dashboard/platform-icon"
import { PLATFORM_LABELS, STATUS_STYLES as POST_STATUS_STYLES } from "@/lib/social-post-constants"
import { csrfToken } from "@/lib/csrf"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { OptimizedImage } from "@/components/ui/optimized-image"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"

const LISTING_TYPE_LABELS: Record<Listing["listing_type"], string> = {
  sale: "For sale",
  long_let: "Long let",
  short_let: "Short let",
  holiday_let: "Holiday let",
}

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  house: "House",
  flat: "Flat",
  apartment: "Apartment",
  bungalow: "Bungalow",
  cottage: "Cottage",
  studio: "Studio",
  detached: "Detached",
  semi_detached: "Semi-detached",
  terraced: "Terraced",
  maisonette: "Maisonette",
  land: "Land",
  commercial: "Commercial",
  other: "Other",
}

const STATUS_STYLES: Record<Listing["status"], string> = {
  draft: "bg-stone/60 text-slate",
  active: "bg-charcoal text-white",
  archived: "bg-warm-grey/20 text-warm-grey",
}

export default function ShowListing({
  listing,
  videos,
  social_posts = [],
  current_role,
  google_maps_key,
  google_maps_map_id,
  connections = [],
}: {
  listing: Listing
  videos: Video[]
  social_posts?: SocialPost[]
  current_role: string
  google_maps_key: string | null
  google_maps_map_id?: string | null
  connections?: SocialConnection[]
}) {
  const canDestroy = current_role === "owner" || current_role === "admin"
  const isShortLet = listing.listing_type === "short_let" || listing.listing_type === "holiday_let"
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const isImporting = listing.scrape_status === "pending" || listing.scrape_status === "scraping"
  const isFailed = listing.scrape_status === "failed"

  // Listen for background description updates and silently reload
  useEffect(() => {
    const subscription = cable.subscriptions.create(
      { channel: "ListingChannel", slug: listing.slug },
      {
        received(data: { event: string }) {
          if (data.event === "descriptions_ready") {
            router.reload({ only: ["listing"] })
          }
        },
      }
    )
    return () => { subscription.unsubscribe() }
  }, [listing.slug])

  return (
    <DashboardLayout>
      <Head title={`${listing.address} | QuiteRight`} />

      {/* Back link */}
      <Link
        href="/dashboard/listings"
        className="mb-6 inline-flex items-center gap-1 text-sm text-warm-grey hover:text-charcoal"
      >
        <svg className="size-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
        </svg>
        Back to listings
      </Link>

      {/* Header */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-charcoal">
            {listing.address}
          </h1>
          {listing.price && (
            <p className="mt-1 text-xl font-semibold text-charcoal">{listing.price}</p>
          )}
          {!isImporting && (
            <div className="mt-3 flex items-center gap-2">
              <Badge className={`capitalize ${STATUS_STYLES[listing.status]}`}>
                {listing.status}
              </Badge>
              <Badge className="border-charcoal/15 bg-white text-slate">
                {LISTING_TYPE_LABELS[listing.listing_type]}
              </Badge>
              <span className="text-xs text-warm-grey">
                Updated {formatRelativeTime(listing.updated_at)}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isImporting && !isFailed && (
            <>
              <ShareListingDialog listing={listing} connections={connections} />
              <Button asChild variant="secondary">
                <Link href={`/dashboard/listings/${listing.slug}/edit`}>Edit</Link>
              </Button>
            </>
          )}
          {canDestroy && <DeleteButton listingSlug={listing.slug} />}
        </div>
      </div>

      {/* Import progress — hero state when importing or failed */}
      {(isImporting || isFailed) && (
        <ImportProgress listingSlug={listing.slug} scrapeStatus={listing.scrape_status} />
      )}

      {/* All content sections — hidden during import */}
      {!isImporting && (
        <>
          {/* === VIDEOS SECTION === */}
          <div className="mb-8">
            <h2 className="mb-4 font-display text-lg font-semibold text-charcoal">
              Videos
            </h2>

            {videos.length === 0 ? (
              <button
                type="button"
                onClick={() => {
                  track("video_created", { listing_slug: listing.slug })
                  router.post(`/dashboard/listings/${listing.slug}/videos`)
                }}
                className="group relative w-full cursor-pointer rounded-xl border-2 border-dashed border-stone bg-cream/40 px-6 py-14 text-center transition-all duration-200 hover:border-red/40 hover:bg-cream/70"
              >
                {/* Decorative corner accents */}
                <div className="pointer-events-none absolute left-3 top-3 size-4 border-l-2 border-t-2 border-warm-grey/20 rounded-tl-sm transition-colors duration-200 group-hover:border-red/30" />
                <div className="pointer-events-none absolute right-3 top-3 size-4 border-r-2 border-t-2 border-warm-grey/20 rounded-tr-sm transition-colors duration-200 group-hover:border-red/30" />
                <div className="pointer-events-none absolute bottom-3 left-3 size-4 border-b-2 border-l-2 border-warm-grey/20 rounded-bl-sm transition-colors duration-200 group-hover:border-red/30" />
                <div className="pointer-events-none absolute bottom-3 right-3 size-4 border-b-2 border-r-2 border-warm-grey/20 rounded-br-sm transition-colors duration-200 group-hover:border-red/30" />

                <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-xl bg-stone/30 transition-colors duration-200 group-hover:bg-red/10">
                  <svg className="size-7 text-warm-grey transition-colors duration-200 group-hover:text-red/70" fill="none" viewBox="0 0 24 24" strokeWidth={1.25} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                  </svg>
                </div>
                <p className="font-display text-base font-semibold text-charcoal">No videos yet</p>
                <p className="mx-auto mt-1.5 max-w-xs text-[13px] leading-relaxed text-warm-grey">
                  Turn this listing into a scroll-stopping video ad.
                </p>
                <div className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-red px-4 py-2 text-sm font-medium text-white transition-colors duration-200 group-hover:bg-deep-red">
                  <svg className="size-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  Create video
                </div>
              </button>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {videos.map((video) => (
                  <VideoCard key={video.id} video={video} currentRole={current_role} />
                ))}
                <button
                  type="button"
                  onClick={() => {
                    track("video_created", { listing_slug: listing.slug })
                    router.post(`/dashboard/listings/${listing.slug}/videos`)
                  }}
                  className="group flex min-h-[160px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-stone/60 bg-cream/30 transition-all duration-200 hover:border-red/40 hover:bg-cream/60"
                >
                  <div className="mb-2 flex size-10 items-center justify-center rounded-lg bg-stone/20 transition-colors duration-200 group-hover:bg-red/10">
                    <svg className="size-5 text-warm-grey transition-colors duration-200 group-hover:text-red/70" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-warm-grey transition-colors duration-200 group-hover:text-charcoal">
                    New video
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* Image filmstrip */}
          {listing.images_data.length > 0 && (
            <ImageFilmstrip
              images={listing.images_data}
              address={listing.address}
              onImageClick={setLightboxIndex}
            />
          )}

          {lightboxIndex !== null && (
            <ImageLightbox
              images={listing.images_data}
              initialIndex={lightboxIndex}
              listingSlug={listing.slug}
              canDelete
              onClose={() => setLightboxIndex(null)}
            />
          )}

          {/* Details grid */}
          <div className="mb-8 rounded-xl border border-stone/40 bg-white p-6">
            <h2 className="mb-4 font-display text-lg font-semibold text-charcoal">
              Property details
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {listing.property_type && (
                <DetailItem label="Type" value={PROPERTY_TYPE_LABELS[listing.property_type] || listing.property_type} />
              )}
              <DetailItem label="Listing type" value={LISTING_TYPE_LABELS[listing.listing_type]} />
              {listing.bedrooms != null && (
                <DetailItem label="Bedrooms" value={listing.bedrooms.toString()} />
              )}
              {listing.bathrooms != null && (
                <DetailItem label="Bathrooms" value={listing.bathrooms.toString()} />
              )}
            </div>
          </div>

          {/* Description */}
          {listing.description && (
            <div className="mb-8 rounded-xl border border-stone/40 bg-white p-6">
              <h2 className="mb-4 font-display text-lg font-semibold text-charcoal">
                Description
              </h2>
              <div className="prose prose-sm max-w-none text-slate prose-headings:font-display prose-headings:text-charcoal prose-strong:text-charcoal prose-ul:list-disc prose-ol:list-decimal">
                <Markdown>{listing.description}</Markdown>
              </div>
            </div>
          )}

          {/* Features */}
          {listing.features.length > 0 && (
            <div className="mb-8 rounded-xl border border-stone/40 bg-white p-6">
              <h2 className="mb-4 font-display text-lg font-semibold text-charcoal">
                Features
              </h2>
              <div className="flex flex-wrap gap-2">
                {listing.features.map((feature) => (
                  <Badge key={feature} variant="secondary">{feature}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Short-let details */}
          {isShortLet && (listing.nightly_rate || listing.min_nights || listing.availability_url) && (
            <div className="mb-8 rounded-xl border border-stone/40 bg-white p-6">
              <h2 className="mb-4 font-display text-lg font-semibold text-charcoal">
                Short-let details
              </h2>
              <div className="grid gap-4 sm:grid-cols-3">
                {listing.nightly_rate && (
                  <DetailItem label="Nightly rate" value={`£${listing.nightly_rate}`} />
                )}
                {listing.min_nights && (
                  <DetailItem label="Minimum nights" value={listing.min_nights.toString()} />
                )}
                {listing.availability_url && (
                  <div>
                    <p className="text-xs font-medium text-warm-grey">Availability</p>
                    <a
                      href={listing.availability_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 text-sm text-red hover:underline"
                    >
                      View availability
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Location map */}
          {listing.latitude && listing.longitude && (
            <ListingMap
              latitude={Number(listing.latitude)}
              longitude={Number(listing.longitude)}
              address={listing.address}
              apiKey={google_maps_key}
              mapId={google_maps_map_id}
            />
          )}

          {/* Social posts */}
          {social_posts.length > 0 && (
            <ListingSocialPosts posts={social_posts} />
          )}

          {/* Source URL */}
          {listing.source_url && (
            <div className="mb-8 rounded-xl border border-stone/40 bg-white p-6">
              <h2 className="mb-4 font-display text-lg font-semibold text-charcoal">
                Source
              </h2>
              <a
                href={listing.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-red hover:underline"
              >
                View original listing
                <svg className="size-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
              </a>
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  )
}

// --- Helpers ---

function formatRelativeTime(dateStr: string | Date): string {
  const date = typeof dateStr === "string" ? new Date(dateStr) : dateStr
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return "just now"
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
}

function ImageFilmstrip({
  images,
  address,
  onImageClick,
}: {
  images: Array<{ signed_id: string; url: string; thumb_url: string; description?: string }>
  address: string
  onImageClick: (index: number) => void
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const checkScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 2)
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    checkScroll()
    el.addEventListener("scroll", checkScroll, { passive: true })
    const observer = new ResizeObserver(checkScroll)
    observer.observe(el)
    return () => {
      el.removeEventListener("scroll", checkScroll)
      observer.disconnect()
    }
  }, [checkScroll])

  function scroll(direction: "left" | "right") {
    const el = scrollRef.current
    if (!el) return
    const amount = el.clientWidth * 0.7
    el.scrollBy({ left: direction === "left" ? -amount : amount, behavior: "smooth" })
  }

  return (
    <div className="group/strip relative mb-8">
      {/* Scroll container */}
      <div
        ref={scrollRef}
        className="flex h-52 gap-1.5 overflow-x-auto scroll-smooth rounded-xl scrollbar-none sm:h-60"
        style={{ scrollSnapType: "x mandatory" }}
      >
        {images.map((img, i) => (
          <button
            key={img.signed_id}
            type="button"
            onClick={() => onImageClick(i)}
            className="group/img relative h-full shrink-0 cursor-pointer overflow-hidden rounded-lg bg-stone/20"
            style={{
              scrollSnapAlign: "start",
              width: i === 0 ? "clamp(280px, 40%, 400px)" : "clamp(160px, 22%, 260px)",
            }}
          >
            <OptimizedImage
              src={img.thumb_url}
              alt={`${address}, photo ${i + 1}`}
              className="size-full object-cover transition-transform duration-300 group-hover/img:scale-105"
            />

            {/* Description overlay */}
            {img.description && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-full bg-gradient-to-t from-charcoal/85 via-charcoal/50 to-transparent px-3 pb-2.5 pt-8 transition-transform duration-300 ease-out group-hover/img:translate-y-0">
                <p className="text-[11px] leading-snug text-white/90 drop-shadow-sm">
                  {img.description}
                </p>
              </div>
            )}

            {/* Index badge */}
            <span className="absolute right-1.5 top-1.5 rounded bg-charcoal/60 px-1.5 py-0.5 text-[9px] font-semibold tabular-nums text-white/80 backdrop-blur-sm">
              {i + 1}/{images.length}
            </span>
          </button>
        ))}
      </div>

      {/* Left arrow */}
      {canScrollLeft && (
        <button
          type="button"
          onClick={() => scroll("left")}
          className="absolute left-2 top-1/2 z-10 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md backdrop-blur-sm transition-all hover:bg-white hover:shadow-lg"
        >
          <svg className="size-4 text-charcoal" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
        </button>
      )}

      {/* Right arrow */}
      {canScrollRight && (
        <button
          type="button"
          onClick={() => scroll("right")}
          className="absolute right-2 top-1/2 z-10 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md backdrop-blur-sm transition-all hover:bg-white hover:shadow-lg"
        >
          <svg className="size-4 text-charcoal" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
          </svg>
        </button>
      )}

      {/* Edge fade indicators */}
      {canScrollLeft && (
        <div className="pointer-events-none absolute inset-y-0 left-0 w-12 rounded-l-xl bg-gradient-to-r from-cream/80 to-transparent" />
      )}
      {canScrollRight && (
        <div className="pointer-events-none absolute inset-y-0 right-0 w-12 rounded-r-xl bg-gradient-to-l from-cream/80 to-transparent" />
      )}
    </div>
  )
}

function ListingSocialPosts({ posts }: { posts: SocialPost[] }) {
  const [selectedPost, setSelectedPost] = useState<SocialPost | null>(null)

  function handleDelete(post: SocialPost) {
    return new Promise<void>((resolve) => {
      router.delete(`/dashboard/posts/${post.id}`, {
        preserveScroll: true,
        onSuccess: () => toast.success("Post deleted"),
        onFinish: () => resolve(),
      })
    })
  }

  async function handleRetry(post: SocialPost) {
    try {
      const response = await fetch(`/dashboard/posts/${post.id}/retry`, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken() },
      })
      if (response.ok) {
        track("social_post_retried", { platform: post.platform })
        toast.success("Retrying publish...")
        router.reload()
      } else {
        toast.error("Could not retry")
      }
    } catch {
      toast.error("Something went wrong")
    }
  }

  return (
    <div className="mb-8">
      <h2 className="mb-4 font-display text-lg font-semibold text-charcoal">
        Social posts
      </h2>
      <div className="space-y-2">
        {posts.map((post) => {
          const statusStyle = POST_STATUS_STYLES[post.status] || POST_STATUS_STYLES.pending

          return (
            <div
              key={post.id}
              className="cursor-pointer rounded-xl border border-stone/30 bg-white px-4 py-3 transition-colors hover:bg-cream/30"
              onClick={() => setSelectedPost(post)}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <PlatformIcon platform={post.platform} className="size-4 shrink-0" />
                  <span className="text-sm font-medium text-charcoal">
                    {PLATFORM_LABELS[post.platform] || post.platform}
                  </span>
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${statusStyle.className}`}
                  >
                    {statusStyle.label}
                  </Badge>
                </div>

                <div className="flex items-center gap-2">
                  {post.status === "published" && post.platform_post_url && (
                    <a
                      href={post.platform_post_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-red hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      View
                      <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                      </svg>
                    </a>
                  )}
                </div>
              </div>

              {post.caption && (
                <p className="mt-1.5 line-clamp-2 text-xs text-warm-grey">
                  {post.caption}
                </p>
              )}

              <div className="mt-1.5 flex items-center gap-3">
                {post.status === "scheduled" && post.scheduled_at && (
                  <span className="text-[11px] text-warm-grey/60">
                    Scheduled for{" "}
                    {new Date(post.scheduled_at).toLocaleString("en-GB", {
                      day: "numeric",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                )}
                {post.status === "published" && post.published_at && (
                  <span className="text-[11px] text-warm-grey/60">
                    Published{" "}
                    {new Date(post.published_at).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                )}
              </div>

              {post.status === "failed" && post.error_message && (
                <p className="mt-1.5 text-xs text-red/80">{post.error_message}</p>
              )}

              {post.status === "published" && (
                <SocialPostMetrics metrics={post.metrics} />
              )}
            </div>
          )
        })}
      </div>

      <SocialPostDetailDialog
        post={selectedPost}
        open={!!selectedPost}
        onOpenChange={(open) => !open && setSelectedPost(null)}
        onDelete={handleDelete}
        onRetry={handleRetry}
        showVideoLink
      />
    </div>
  )
}

// --- Platform Icons ---

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-medium text-warm-grey">{label}</p>
      <p className="mt-1 text-sm font-medium text-charcoal">{value}</p>
    </div>
  )
}


function DeleteButton({ listingSlug }: { listingSlug: string }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="secondary">
          Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete listing?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete the listing and all associated images. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={() => {
              track("listing_deleted", { listing_slug: listingSlug })
              router.delete(`/dashboard/listings/${listingSlug}`)
            }}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
